<template>
  <div class="fts-form-editor">
    <fts-popper
      trigger="clickToOpen"
      placement="top-start"
      :prevent-overflow-boundary="elParentId"
      prevent-overflow-alt-axis
      prevent-overflow-tether-off
      :offset="[0, 0]"
      :reference-id="uniqueKey"
      :content-class="`form-editor-dropdown ${uniqueKey}`"
      ref="popper"
    >
      <template #content>
        <div class="form-editor-toolbar">
          <template v-for="x in EDITOR_TOOLS">
            <fts-tippy
              :key="x.id"
              v-if="!x.beta || (x.beta && isBeta)"
            >
              <div slot="content">
                {{x.name}} <fts-release-badge v-if="x.beta" />
              </div>
              <template>
                <!-- foreground -->
                <fts-color-picker
                  v-model="foreColor"
                  :colors="foreColors"
                  :class="uniqueKey"
                  @input="val => setColor(val, 'forecolor')"
                  tabindex="0"
                  v-if="x.id === 'forecolor'"
                >
                  <button type="button">
                    <div class="form-editor-forecolor">A</div>
                  </button>
                </fts-color-picker>
                <!-- background -->
                <fts-color-picker
                  v-model="backColor"
                  :colors="backColors"
                  :class="uniqueKey"
                  @input="val => setColor(val, 'backcolor')"
                  tabindex="0"
                  v-else-if="x.id === 'backcolor'"
                >
                  <button type="button">
                    <div class="form-editor-backcolor">A</div>
                  </button>
                </fts-color-picker>
                <!-- link -->
                <button
                  type="button"
                  @click="openLinkModal"
                  v-else-if="x.id === 'link'"
                >
                  <font-awesome-icon :icon="x.icon" v-if="x.icon" />
                </button>
                <!-- default -->
                <button type="button" @click="contentEdit(x.id)" v-else>
                  <font-awesome-icon :icon="x.icon" v-if="x.icon" />
                  <template v-else>{{ x.text }}</template>
                </button>
              </template>
            </fts-tippy>
          </template>
        </div>
      </template>
      <div
        slot="reference"
        v-once
        v-html="mutableContent"
        contenteditable
        @input="contentOnInput"
        @paste="contentOnPaste"
        @blur="contentOnBlur"
        tabindex="0"
        ref="editor"
      ></div>
    </fts-popper>
    <!-- link modal -->
    <fts-modal
      class="designer-modal"
      header-class="designer-header"
      content-class="designer-body"
      footer-class="designer-footer"
      :backdrop="false"
      :class="uniqueKey"
      @close="closeLinkModal"
      v-if="showLinkModal"
    >
      <template #header>
        <div class="designer-header-wrapper">
          <div class="designer-header-title">Content Link</div>
        </div>
        <fts-btn
          text
          icon
          class="designer-header-action"
          @click="closeLinkModal"
        >
          <font-awesome-icon :icon="['fas', 'times']" />
        </fts-btn>
      </template>
      <template #body>
        <div class="fts-form-group">
          <fts-form-label placement="top">URL</fts-form-label>
          <input
            type="text"
            class="fts-form-control"
            v-model="linkModalFormData.link"
          />
        </div>
        <div class="fts-form-group">
          <fts-form-label placement="top">Title (optional)</fts-form-label>
          <input
            type="text"
            class="fts-form-control"
            v-model="linkModalFormData.label"
          />
        </div>
      </template>
    </fts-modal>
  </div>
</template>

<script>
import { authorizationStore } from '../../store/authorizationStore';
import { store as canvasDesignStore } from '../../store/canvasDesignStore';
import commons from '../../commons/commons';
import { darkPalette, lightPalette } from '../../commons/colors';

import FTSBtn from "../FTSBtn";
import FTSColorPicker from '../FTSColorPicker';
import FTSFormLabel from '../FTSFormLabel';
import FTSModal from "../FTSModal";
import FTSPopper from "../FTSPopperNew";
import FTSTippy from "../FTSTippy";

export default {
  components: {
    "fts-btn": FTSBtn,
    "fts-color-picker": FTSColorPicker,
    "fts-form-label": FTSFormLabel,
    "fts-modal": FTSModal,
    "fts-popper": FTSPopper,
    "fts-tippy": FTSTippy
  },
  props: {
    elParentId: String,
    content: String
  },
  data: () => ({
    uniqueKey: null,
    EDITOR_TOOLS: Object.freeze([
      { id: "bold", name: "Bold (Ctrl+B)", icon: ["fas", "bold"] },
      { id: "italic", name: "Italic (Ctrl+I)", icon: ["fas", "italic"] },
      { id: "underline", name: "Underline (Ctrl+U)", icon: ["fas", "underline"] },
      { id: "strikeThrough", name: "Strikethrough", icon: ["fas", "strikethrough"] },
      { id: "justifyLeft", name: "Align left", icon: ["fas", "align-left"], beta: true },
      { id: "justifyRight", name: "Align right", icon: ["fas", "align-right"], beta: true },
      { id: "justifyCenter", name: "Align center", icon: ["fas", "align-center"], beta: true },
      { id: "justifyFull", name: "Justify", icon: ["fas", "align-justify"], beta: true },
      { id: "indent", name: "Increase indent", icon: ["fas", "indent"], beta: true },
      { id: "outdent", name: "Decrease indent", icon: ["fas", "outdent"], beta: true },
      { id: "insertUnorderedList", name: "Bulleted list", icon: ["fas", "list-ul"] },
      { id: "insertOrderedList", name: "Numbered list", icon: ["fas", "list-ol"] },
      { id: "subscript", name: "Subscript", icon: ["fas", "subscript"] },
      { id: "superscript", name: "Superscript", icon: ["fas", "superscript"] },
      { id: "forecolor", name: "Font color", fieldName: "foreColor" },
      { id: "backcolor", name: "Font background color", fieldName: "backColor" },
      { id: "h1", name: "Heading 1", text: "H1" },
      { id: "h2", name: "Heading 2", text: "H2" },
      { id: "link", name: "Link", icon: ["fas", "link"], beta: true },
      { id: "unlink", name: "Remove Link", icon: ["fas", "unlink"], beta: true },
      { id: "p", name: "Paragraph", icon: ["fas", "paragraph"] },
      { id: "table", name: "Table", icon: ["fas", "table"], beta: true },
    ]),
    /* colors */
    foreColors: darkPalette.concat([""]),
    backColors: lightPalette.concat([""]),
    foreColor: null,
    backColor: null,
    textSelection: null,
    showLinkModal: false,
    linkModalFormData: {}
  }),
  methods: {
    getSelection() {
      if (commons.elementContainClassName(document.activeElement, this.uniqueKey)) {
        return;
      }

      try {
        const sel = window.getSelection();
        const range = sel.getRangeAt(0);
        if (sel.isCollapsed) {
          throw "no any text selected";
        } else if (range.endOffset - range.startOffset >= 0) {
          this.textSelection = range;
        } else if (document.selection && document.selection.createRange) {
          this.textSelection = document.selection.createRange();
        } else {
          throw "error";
        }
      } catch(err) {
        this.textSelection = null;
      }
    },
    contentOnInput(evt) {
      this.mutableContent = evt.target.innerHTML;
      try {
        this.$refs.popper.popperInstance.update();
      } catch(err) { console.error(err); }
      this.$emit("content:input");
    },
    contentOnPaste(evt) {
      evt.preventDefault();
      document.execCommand("insertHTML", false, evt.clipboardData.getData("text/plain"));
      this.$emit("content:paste");
    },
    contentOnBlur() {
      this.$emit("content:blur");
    },
    contentEdit(command) {
      if (command == "h1" || command == "h2" || command == "p") {
        document.execCommand("formatBlock", false, command);
      } else if (command == "table") {
        var column = parseInt(prompt("Please enter number of column", "2"));
        var row = parseInt(prompt("Please enter number of row", "2"));
        var internal =
          '<table style="border: 1px solid black; border-collapse: collapse;">';
        var i, j;
        for (i = 0; i < row; i++) {
          internal += "<tr>";
          for (j = 0; j < column; j++) {
            internal += '<td style="border: 1px solid black;">type text here...</td>';
          }
          internal += "</tr>";
        }
        internal += "</table>";
        document.execCommand("insertHTML", false, internal);
      } else {
        document.execCommand(command);
      }

      this.$refs.editor.focus();
    },
    updateContent(commandName, showDefaultUI, val, additional) {
      let sel = document.getSelection();
      sel.removeAllRanges();
      sel.addRange(this.textSelection);
      /* exec command */
      document.execCommand(commandName, showDefaultUI, val);
      /* create link */
      if (commandName === "createlink") {
        let el = sel.focusNode.parentElement;
        const link = el.getAttribute("href");
        if (link) {
          el.setAttribute(
            "href",
            link.toLowerCase().startsWith("http") ? link : `//${link}`
          );
          el.target = '_blank';
          el.classList.add('fts-link-primary');
          if (additional && additional.label) {
            el.innerText = additional.label;
          }
        }
      }
      /* reset */
      this.textSelection = null;
      /* update value */
      this.mutableContent = this.$refs.editor.innerHTML;

      this.$nextTick(() => {
        this.$refs.editor.focus();
      });
    },
    setColor(evt, type) {
      if (!evt) evt = type === "forecolor" ? "#000" : "transparent";
      this.updateContent(type, false, evt);

      this.foreColor = null;
      this.backColor = null;
    },
    openLinkModal() {
      let link = null;
      let text = window.getSelection().toString();
      if (
        this.textSelection &&
        this.textSelection.commonAncestorContainer &&
        this.textSelection.commonAncestorContainer.parentElement
      ) {
        if (this.textSelection.commonAncestorContainer.parentElement.text) {
          text = this.textSelection.commonAncestorContainer.parentElement.text;
        }
        link = this.textSelection.commonAncestorContainer.parentElement.getAttribute("href");
      }

      if (Boolean(text)) {
        if (link) {
          this.$set(this.linkModalFormData, "link", link.startsWith("//") ? link.substring(2) : link);
        }
        this.$set(this.linkModalFormData, "label", text);
        this.showLinkModal = true;
      }
    },
    closeLinkModal() {
      this.showLinkModal = false;
      if (this.linkModalFormData.url || this.linkModalFormData.label) {
        this.updateContent(
          "createlink",
          false,
          this.linkModalFormData && this.linkModalFormData.link,
          {
            label: (this.linkModalFormData.label || this.linkModalFormData.url).trim()
          }
        );
      }
      /* reset */
      this.linkModalFormData = {};
    }
  },
  computed: {
    isBeta() {
      return authorizationStore.getters.isBeta || canvasDesignStore.getters.workspaceBeta;
    },
    mutableContent: {
      get() {
        return this.content;
      },
      set(val) {
        this.$emit("input", val);
        this.$emit("update:content", val);
      }
    }
  },
  mounted() {
    this.uniqueKey = `form-editor-${this._uid}`;
    document.addEventListener('selectionchange', this.getSelection);
  },
  beforeDestroy() {
    document.removeEventListener('selectionchange', this.getSelection);
  }
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-form-editor {
  color: var(--fts-text);
}
.form-editor-dropdown {
  padding: 0.25rem;
  .form-editor-toolbar {
    @include flex-gap($gap: 0.25rem);
    button {
      height: 30px;
      width: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid var(--fts-border);
      border-radius: 0.25rem;
      background-color: var(--fts-card-background);
      color: var(--fts-text);
      font-weight: 500;
      font-size: 0.875rem;
      cursor: pointer;
    }
    .form-editor-forecolor {
      display: inline-flex;
      border-bottom: 2px solid var(--fts-text);
    }
    .form-editor-backcolor {
      width: 1rem;
      border-radius: 0.15rem;
      background-color: var(--fts-text);
      color: var(--fts-card-background);
      line-height: 1rem;
    }
  }
}
</style>